export interface BlogPost {
  slug: string;
  title: string;
  date: string;
  excerpt: string;
  content: string;
}

export const posts: BlogPost[] = [
  {
    slug: 'our-first-summer-on-lake-lanier',
    title: 'Our First Summer on the Lake: Deer, Dock Days, and a Half-Mile Zipline',
    date: '2026-07-15',
    excerpt:
      'Our first summer trip to Reel Easy Retreat — ten days of perfect lake temps, sunsets, deer in the yard, red-hot dock fishing, and a stack of North Georgia excursions from ziplining in Helen to tubing the Chestatee.',
    content: `
Every trip up to this point had a to-do list attached to it. Beds to build, art to hang, a game room to fill, Wi-Fi to run to the dock. This one was different. From **June 18 to June 27**, we came up for our **first real summer trip** — no freight on the porch, no boxes to break down, just ten days of actually *living* in the house we built.

It did not disappoint.

## The House Delivered on Its Promise

When we bought this place, we had a picture in our heads of what it would feel like in full summer. This trip, we got to stand in it. The trees were fully leafed out and deep green, wrapping the house so it felt exactly like the thing we'd hoped for: **a cabin in the woods, right on the water.** Private, shaded, quiet — and then you walk out to the dock and the whole lake opens up in front of you.

The lake temperature was *perfect*. Warm enough to stay in for hours, cool enough to be refreshing. And the sunsets over the cove were genuinely gorgeous night after night.

## Dock Life

If you only did one thing on a trip here, it should be this: nothing.

We spent hours hanging on the dock and floating in the water right off of it — **streaming music** (that dock Wi-Fi we ran on trip two more than earned its keep), swimming, and just having a great time. It's the kind of easy, unstructured fun that a lake house is supposed to be about, and it was the heart of the whole trip.

The **deer were everywhere** around the house — a regular part of morning coffee and evening winding-down. It never got old.

## The Fishing Was Hot

We are called Reel Easy Retreat for a reason, and this trip made us look good. The bite **right off the dock** was on fire the entire time. We pulled in:

- **Largemouth bass**
- **Bluegill**
- **Flathead catfish**
- **Channel cats**

All of it caught steps from the back door — no boat required. Bring your rods.

## The Excursions

We also made a point of getting out and exploring the area, and every trip out was a winner:

- **Ziplining in Helen with [Nacoochee Adventures](https://nacoocheeadventures.com/)** — their course includes a roughly **half-mile-long run down the hillside**, and it was an absolute blast. Easily a highlight.
- **Preacher's Rock hike** — a stretch of the **Appalachian Trail** that rewards you with fabulous long-range mountain views at the top. Worth the climb.
- **DeSoto Falls** — a pretty little waterfall hike that was worth checking out. We still give the edge to Dick's Creek Falls from our last trip for the more exciting payoff, but DeSoto is an easy, scenic add-on to a mountain day.
- **[Wolf Mountain Vineyards](https://www.wolfmountainvineyards.com/)** — great wine and even better views from the top of the mountain. A perfect slow afternoon.
- **Dahlonega** — we wandered the historic square, poked through the shops, and grabbed a bite and a beer at **Shenanigans Irish Pub**. Good food, good beer, easy vibe.
- **Tubing the Chestatee River with [Appalachian Outfitters](https://www.appalachianoutfitters.com/)** — a lazy, sunny float down the Chestatee, which flows straight into Lake Lanier. Best part: it's only about **24 minutes from the house**, right on the way to Dahlonega, so it pairs perfectly with a day in town.

## We're Already Going Back

We loved this trip so much that we're heading right back up **this weekend (July 18–25)** for another round. Perfect water, sunsets, deer in the yard, red-hot dock fishing, and a mountain full of things to do a short drive away — it turns out the house is exactly what we hoped it would be in summer.

If you're thinking about a warm-weather trip to Lake Lanier, this is the season we built this place for. [Come see it for yourself.](https://www.airbnb.com/rooms/1667638070507956091)
    `.trim(),
  },
  {
    slug: 'things-to-do-on-lake-lanier',
    title: 'Best Things to Do on Lake Lanier (Beyond Just Swimming)',
    date: '2026-04-27',
    excerpt:
      'Most people come to Lake Lanier for the water — but the best trips combine the lake with the mountain towns, waterfalls, and local spots that surround it. Here\'s what we tell every guest.',
    content: `
Most people come to Lake Lanier for the water — but the best trips combine the lake with the mountain towns, waterfalls, and local spots that surround it. Here's what we tell every guest who stays at Reel Easy Retreat.

## On the Water

The obvious start: Lake Lanier is Georgia's premier recreational lake, and for good reason. With 692 miles of shoreline, there's no shortage of coves to explore, especially by kayak. We keep kayaks at the dock for guests — paddle north toward the quieter coves in the morning before the wake boats come out.

If you want something bigger, several marinas nearby rent pontoons, ski boats, and waverunners by the half-day or full day. [Lanier Luxury Boat Rentals](https://lanierluxuryboatrentals.com/) and [Best in Boating](https://www.bestinboating.com/boat-rentals/lake-lanier/) are both well-reviewed and close to the north shore.

## Waterfront Dining

Skip the drive — eat on the water. [Skogie's Lakefront Restaurant](https://skogieslakefrontrestaurant.us/) is the closest waterfront spot to Reel Easy Retreat and the most consistent. Great atmosphere, cold drinks, solid food. [Pelican Pete's](https://www.pelicanpetes.com/) is a massive floating restaurant that has to be seen to be believed.

## Day Trip: Helen, GA

About 45 minutes northeast, Helen is a Bavarian-themed alpine village that sounds kitschy and absolutely delivers. Tubing on the Chattahoochee River runs right through town. In fall, Oktoberfest draws massive crowds — book early if you're visiting September through November.

## Waterfalls Within an Hour

The mountains north of the lake are loaded with waterfall hikes. Our favorites:

- **Dick's Creek Falls** — a short, easy hike with a deep swimming hole at the bottom. Perfect for kids and adults alike in summer.
- **DeSoto Falls** — a USFS recreation area with two falls and a campground. The upper falls are the payoff.
- **Duke's Creek Falls** — a longer hike but worth every step. One of the most dramatic falls in North Georgia.

## Local Food Worth Knowing

[Smokin' Mike's Meats](https://www.smokinmikesmeats.com/) is the closest restaurant to the house and one of the best BBQ spots in the region. Do not skip it. For something different, Brookton Catfish School has no website, no social media presence, and a parking lot full of trucks every weekend — that's all you need to know.

## Dahlonega for a Morning

Twenty minutes north, Dahlonega was the site of America's first major gold rush. The historic square has good coffee, gold panning for the kids, wineries, and the [Consolidated Gold Mine](http://consolidatedgoldmine.com/) tour. Pair it with a stop at one of the North Georgia vineyards on the way back — [Kaya Vineyards](https://www.kayavineyards.com/) and [Three Sisters Vineyards](http://www.threesistersvineyards.com/) are both excellent.

---

*Staying at Reel Easy Retreat? We put together a full local guide with links for every recommendation in this post — it's in the welcome book at the house, and on our [home page](/).*
    `.trim(),
  },
  {
    slug: 'we-are-live-on-airbnb',
    title: 'We\'re Live: Reel Easy Retreat Is Open and Booking Fast',
    date: '2026-04-20',
    excerpt:
      'After three build-out trips and a lot of late nights, the listing is live on Airbnb — and we\'re running inaugural pricing to fill out our first calendar. The early dates are already moving.',
    content: `
It's official: [Reel Easy Retreat is live on Airbnb](https://www.airbnb.com/rooms/1667638070507956091) and taking reservations.

After three build-out trips, a lot of cardboard, a lot of long days, and one memorable 1 a.m. arrival, the house is ready and the calendar is open. We hit "publish" earlier this month, and the response has been faster than we expected.

## Inaugural Pricing

Because this is our first season, we're running **inaugural pricing** — meaningful discounts off what we plan to charge once we have a stack of reviews and a full year of calendar data behind us. The thinking is simple: we'd rather have a house full of happy early guests telling their friends about us than a half-empty calendar with the "right" rate.

That means our opening guests get a four-bedroom waterfront house — private dock, kayaks, game room, loft, screened porch, dock Wi-Fi, the whole thing — at a price that won't be available again once the season fills in.

## The Calendar Is Moving

We weren't sure what to expect when the listing went live. We're new. We have no reviews yet. The photos are good but it's still a brand-new listing on a platform with a lot of options.

Turns out: people are booking. Weekends are going first, as expected, but a few weeklong stays are already on the books too. If you've been thinking about a Lake Lanier trip this summer or fall, the dates you actually want are filling in faster than we thought they would. Don't sit on it.

## What You're Booking

Quick recap, since this is the first post written from the "we have guests coming" side of the line:

- **Four bedrooms**, each with its own feel — pick the one that fits your trip.
- **Private dock** on a quiet cove, with kayaks ready to go and reliable Wi-Fi all the way down to the water.
- **Game room and loft** for kids, rainy afternoons, or anyone who needs a break from the rest of the group.
- **Screened porch and outdoor dining** for long evenings when nobody wants to go inside.
- **A location that punches above its weight** — the lake out the back door, real mountains thirty minutes north, waterfalls within an hour, BBQ that ruins you for other BBQ, and Helen, Dahlonega, and a stack of vineyards all close enough for a day trip.

## How to Book

The fastest way is right on Airbnb: [view the listing and check dates here](https://www.airbnb.com/rooms/1667638070507956091). The "Book on Airbnb" buttons on our [home page](/) go to the same place.

If you've got questions about the house, the area, or whether a specific weekend will work for what you have in mind, reach out — we read every message.

We've been working toward this day for months. We're proud of what this house has become, and we cannot wait to start handing the keys over.
    `.trim(),
  },
  {
    slug: 'build-out-trip-two',
    title: 'Trip Two: The House Comes Together (And We Finally Get to Eat Somewhere)',
    date: '2026-03-17',
    excerpt:
      'Second build-out trip in the books. Bedrooms, loft, game room, grill, dock Wi-Fi — and our first real chance to step away from the project and remember why we fell for this area in the first place.',
    content: `
Second build-out trip in the books. The house looks like a house now.

When we left Florida this time, we knew what we were driving into — another front porch stacked with deliveries, another week of long days unboxing, assembling, hanging, mounting, and hauling cardboard to the road. What we didn't quite expect was how much faster the work goes when you're not also figuring out where the light switches are.

## What Came Together This Trip

- **All four bedrooms** are now fully built out. Beds made, art hung, nightstands stocked. Each room has its own feel — we wanted guests to be able to "pick a room" and have it actually mean something.
- **The game room** went from empty drywall to one of our favorite spaces in the house. We won't spoil the full reveal here.
- **The loft** is finished — comfy seating and a cozy upstairs hangout for kids, or adults who just want a quiet spot away from the action.
- **The grill and outdoor dining area** are assembled, in place, and ready for summer.
- **Dock Wi-Fi** got installed. This one matters more than it sounds — strong, reliable signal all the way down to the dock means you can take a call, stream music, or actually use that smart fish finder without dropping out.

A few rooms still need finishing touches, but the bones of the house are here. Walking through it on the last evening of this trip, we both kept saying the same thing: *this is going to be such a good place for people.*

## We Finally Took a Break

Last trip, we ate every meal at the dining table, mostly because we didn't have the energy to leave the house. This trip, we made ourselves leave — partly to recharge, partly because it would be ridiculous to live this close to so many great places and never go to any of them.

A few highlights:

- **[Skogie's Lakefront Restaurant](https://skogieslakefrontrestaurant.us/)** — closest waterfront spot to the house and an easy default. Cold drinks, lake views, solid food.
- **[Pelican Pete's](https://www.pelicanpetes.com/)** — a giant floating restaurant that has to be seen to be understood. Great vibe on a sunny afternoon.
- **[Dick's Creek Falls](https://www.atlantatrails.com/hiking-trails/dicks-creek-falls/)** — short hike, big payoff. The swimming hole at the bottom is going to be on heavy rotation this summer.
- **[Big Creek Distilling](https://bigcreekdistilling.com/)** — small-batch spirits, friendly tasting room, the kind of place you tell friends about on the drive home.
- **[Smokin' Mike's Meats](https://www.smokinmikesmeats.com/)** — the closest restaurant to the house and one of the best BBQ spots we've found anywhere. Not exaggerating.

Every one of those was a hit. None of them disappointed.

## Why We Love It Here

We've said it before, but it's worth saying again: this part of Georgia has a stupid amount going for it. You're on the lake. You're a short drive from real mountains. There are waterfalls within an hour in pretty much every direction. There's tubing on the Chattahoochee, gold panning in Dahlonega, vineyards on the back roads, and BBQ that rearranges your priorities. From the deck of this house you can hear the cove in the morning and watch the water go quiet in the evening.

Last summer this area sold us. This trip confirmed it.

## What's Left

One more trip. A handful of finishing touches. A walkthrough with our cleaner. Photos for the listings. And then — finally — guests.

We can't wait to share this place.
    `.trim(),
  },
  {
    slug: 'arriving-at-1am-with-no-couches',
    title: 'Arriving at 1 a.m. With No Couches: Our First Night at Reel Easy Retreat',
    date: '2026-02-16',
    excerpt:
      'How Reel Easy Retreat started — a 1 a.m. arrival, a porch full of freight, and a family unboxing beds by phone flashlight in a house that didn\'t yet have a single couch.',
    content: `
We've been quiet on this site for a few weeks because we've been very loud somewhere else: in the driveway of a brand-new-to-us lake house in North Georgia, surrounded by what felt like a pallet truck's worth of cardboard, trying to figure out which box had the bed sheets.

This is the story of how Reel Easy Retreat started — and why our first night here ended sometime around 4 a.m. with all of us asleep on freshly assembled beds, in a house that didn't yet have a single couch.

## The Backstory

Last summer, we took a family trip up to Lake Lanier and the North Georgia mountains. We drove up from Florida expecting "a nice break" and ended up doing the thing nobody plans for: we fell in love with a place. The lake. The mountain towns thirty minutes north. Waterfalls everywhere. Dahlonega's square. Helen on a fall afternoon. Distilleries, vineyards, BBQ joints with no website and a line out the door.

We'd been casually in the market for a waterfront lake home for a while. After that trip, "casually" turned into "actively." A few months later we found this house — four bedrooms, a loft, a game room, a dock on a quiet cove — and the day we closed, we started planning our first build-out trip.

## The Drive Up

We left Florida early, the car full and a trailer behind us, and pulled into the driveway around 1 a.m. The day before, freight company after freight company had rolled up to the front door — beds, mattresses, linens, kitchenware, tools, lamps, rugs — and the porch looked like the warehouse scene from *Raiders of the Lost Ark*.

The house itself was nearly a blank slate. The original owners had left us a beautiful dining room table and chairs that we love and decided to keep — and that was it for finished spaces. Every other room was empty.

## The 1 a.m. Unboxing

All of us, exhausted. No couches. Four bedrooms with mattresses still in shrink wrap. Nothing to do but start cutting tape.

We built beds that night. We dug through stacks of boxes for sheets and pillows. We made up the rooms by phone flashlight. Sometime around 4 a.m. — we still aren't totally sure — everyone finally collapsed.

The funny part: we woke up the next morning in what felt like the best hotel room we'd ever stayed in. Quiet cove out the back windows. Coffee in the new kitchen. A dining room table from the previous owners that we ate breakfast on while looking out over the water.

## A Blank Slate Is a Gift

We could have bought a place that was already turnkey. We didn't, on purpose. Building this house out from scratch — picking every bed, every set of sheets, every piece of art, every dish, every deck chair — has turned out to be one of the best parts of the project. Every room is intentional. Every choice was made for guests we hadn't met yet but already wanted to take care of.

## Why We're Doing This

It's hard to overstate how much we love this part of the country. The lake out the back door. The Blue Ridge mountains thirty minutes up the road. Waterfalls, hiking, tubing, fishing, distilleries, vineyards, gold-rush history, real-deal small-town BBQ — all of it within easy reach. Last summer the area sold us in a week. We bought this house so we could keep coming back, and so we could share it.

We've got two more trips lined up over the next several weeks. The bedrooms still need finishing. The game room is empty. The loft is empty. The dock needs Wi-Fi. The grill and outdoor dining area are still in boxes on the front porch.

But the dream is real, and we are very excited to share it.
    `.trim(),
  },
];
